
import type { Ref, SetupContext } from 'vue'
import type { EpbVirtualScrollerListResizeEvent } from './epb-virtual-scroller-list.component.vue'
import { deepEqual } from '@empathyco/x-utils'
import Sortable from 'sortablejs'
import { defineComponent, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import { GET_PROPS_KEYS, ITEMS_TO_SHOW } from './consts'
import EpbVirtualScrollerList from './epb-virtual-scroller-list.component.vue'

export interface VirtualScrollerSortableListEvent<T = unknown> {
  newIndex: number
  oldIndex: number
  item: T
}

export default defineComponent({
  name: 'EpbVirtualScrollerSortableList',
  components: { EpbVirtualScrollerList },
  props: {
    disabled: Boolean,
    draggableClass: {
      type: String,
      default: undefined,
    },
    ghostClass: {
      type: String,
      default: undefined,
    },
  },
  emits: {
    move: (event: VirtualScrollerSortableListEvent) => {},
    resize: (event: EpbVirtualScrollerListResizeEvent): void => {},
  },
  setup(props, ctx: SetupContext) {
    let sortableList

    const showedItems = inject<Ref<Array<unknown>>>(ITEMS_TO_SHOW)
    const getParentProps = inject<() => { items: Array<unknown> }>(GET_PROPS_KEYS)

    const listRef = ref(null)

    function compareitems(itemA: unknown): (itemB: unknown) => boolean {
      return itemB => deepEqual(itemB, itemA)
    }

    function findIndex(position: number): number {
      const showedItem = showedItems.value.find((_, index) => index === position)
      const { items } = getParentProps()
      return items.findIndex(compareitems(showedItem))
    }

    function existInShowedItems(item: unknown): boolean {
      return showedItems.value.findIndex(compareitems(item)) > 0
    }

    onMounted(() => {
      let item: unknown
      let oldIndex: number

      sortableList = Sortable.create(listRef.value.$el, {
        animation: 500,
        disabled: props.disabled,
        ...(props.draggableClass && {
          handle: `.${props.draggableClass}`,
        }),
        ...(props.ghostClass && {
          ghostClass: props.ghostClass,
        }),
        onStart: (event: VirtualScrollerSortableListEvent<HTMLElement>) => {
          oldIndex = findIndex(event.oldIndex)
          const { items } = getParentProps()
          item = items.at(oldIndex)
        },
        onEnd: (event: VirtualScrollerSortableListEvent<HTMLElement>) => {
          const newIndex = findIndex(event.newIndex)

          if (newIndex !== oldIndex) {
            if (!existInShowedItems(item)) {
              listRef.value.$el.removeChild(event.item)
            }

            ctx.emit('move', { newIndex, item, oldIndex })
          }

          oldIndex = undefined
          item = undefined
        },
      })
    })

    onUnmounted(() => {
      sortableList?.destroy()
    })

    watch(
      () => props.disabled,
      () => {
        sortableList.options.disabled = props.disabled
      },
    )

    return {
      listRef,
    }
  },
})
